import type { Settings, SettingsUpdate } from '@najah/core'
import { api } from './api'

export const settingsApi = {
  read: () => api<Settings>('/settings'),
  update: (body: SettingsUpdate) => api<Settings>('/settings', { method: 'PATCH', body }),
}

export interface UserRow {
  id: string
  username: string
  fullName: string
  phone: string | null
  isActive: boolean
  roleNameAr: string
  storeName: string | null
  lastLoginAt: string | null
  createdAt: string
}

export interface RoleRow {
  id: string
  key: string
  nameAr: string
  permissions: string[]
  isSystem: boolean
}

export const usersApi = {
  list: (query: { page?: number; pageSize?: number; search?: string }) =>
    api<{ items: UserRow[]; total: number; page: number; pageSize: number; totalPages: number }>('/users', {
      query: query as Record<string, string | number | undefined>,
    }),
  roles: () => api<RoleRow[]>('/users/roles'),
  create: (body: Record<string, unknown>) => api<{ id: string }>('/users', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>) =>
    api<{ success: boolean }>(`/users/${id}`, { method: 'PATCH', body }),
  resetPassword: (id: string, newPassword: string) =>
    api<{ success: boolean }>(`/users/${id}/reset-password`, { method: 'POST', body: { newPassword } }),
  remove: (id: string) => api<{ success: boolean }>(`/users/${id}`, { method: 'DELETE' }),
}

export interface StoreRow {
  id: string
  code: string
  nameAr: string
  address: string | null
  phone: string | null
  isActive: boolean
  isDefault: boolean
  registerCount: number
  userCount: number
  stockQty: number
}

export interface RegisterRow {
  id: string
  code: string
  nameAr: string
  isActive: boolean
  storeId: string
  storeName: string
  storeCode: string
  openShift: { id: string; openedAt: string; userName: string | null } | null
}

export interface DeviceRow {
  id: string
  deviceKey: string
  nameAr: string
  platform: string | null
  storeName: string | null
  isActive: boolean
  lastSeenAt: string | null
  syncCursor: string
  salesCount: number
}

export const storesApi = {
  list: () => api<StoreRow[]>('/stores'),
  create: (body: Record<string, unknown>) => api<{ id: string }>('/stores', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>) =>
    api<{ id: string }>(`/stores/${id}`, { method: 'PATCH', body }),
  makeDefault: (id: string) => api<{ success: boolean }>(`/stores/${id}/default`, { method: 'POST' }),
  remove: (id: string) => api<{ success: boolean }>(`/stores/${id}`, { method: 'DELETE' }),
}

export const registersApi = {
  list: () => api<RegisterRow[]>('/registers'),
  create: (body: Record<string, unknown>) => api<{ id: string }>('/registers', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>) =>
    api<{ id: string }>(`/registers/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<{ success: boolean }>(`/registers/${id}`, { method: 'DELETE' }),
}

export const devicesApi = {
  list: () => api<DeviceRow[]>('/devices'),
  update: (id: string, body: Record<string, unknown>) =>
    api<{ id: string }>(`/devices/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<{ success: boolean }>(`/devices/${id}`, { method: 'DELETE' }),
}
